import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  MdFamilyRestroom, MdPerson, MdBadge, MdSchool, MdPayments, MdStickyNote2, MdCheckCircle,
  MdPrint, MdGroupAdd, MdOpenInNew, MdRestartAlt, MdWarningAmber, MdLink, MdEdit, MdCake,
  MdDescription, MdCreateNewFolder, MdKeyboardReturn, MdFolderShared,
} from 'react-icons/md';
import api from '../../../ui/api';
import { useCachedData, TTL, invalidate } from '../../../ui/cache';
import { useAuth } from '../../auth';
import { useNavigate } from '../../navIntent';
import useToastWithSound from '../../../hooks/useToastWithSound';
import PageShell, { Button } from '../../../ui/PageShell';
import Modal from '../../../ui/Modal';
import { Field, Input, Select, Textarea, RadioGroup } from '../../../ui/Form';
import PhoneInput from '../../../ui/PhoneInput';
import SearchableSelect from '../../../ui/SearchableSelect';
import HelpButton from '../../help/HelpButton';
import { formatRegNo } from '../../../ui/labels';
import { formatPhone } from '../../../ui/phone';
import DossierPrint from './DossierPrint';
import * as M from './dossierModel';
import './dossier.css';

const GENDERS = [{ value: 'male', label: 'ذكر' }, { value: 'female', label: 'أنثى' }];
const FAMILY_MODES = [
  { value: 'new', label: 'عائلة جديدة' },
  { value: 'existing', label: 'عائلة مسجّلة (أخ/أخت في المدرسة)' },
];
const money = (v) => `$${Number(v || 0).toLocaleString('en-US')}`;

const draftKey = (uid) => `mtz.dossierDraft.${uid ?? 'anon'}`;

function loadDraft(uid) {
  try {
    const saved = JSON.parse(sessionStorage.getItem(draftKey(uid)) || 'null');
    if (!saved) return null;
    const fresh = M.freshDraft();
    return {
      ...fresh,
      ...saved,
      family: { ...fresh.family, ...saved.family },
      student: { ...fresh.student, ...saved.student },
    };
  } catch { return null; }
}

/** Focus a field by its key ('student.national_id'); wrappers hold the id
 *  for controls that don't take one (phone, searchable selects). */
function focusField(key) {
  const el = document.getElementById(`dz-${key}`);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  const target = el.matches('input,select,textarea,button') ? el : el.querySelector('input,select,textarea,button');
  setTimeout(() => (target || el).focus({ preventScroll: true }), 250);
}

/** One numbered block of the form. */
function Block({ n, icon: Icon, title, extra, children, tone }) {
  return (
    <section className={`dz-block${tone ? ` dz-block-${tone}` : ''}`}>
      <header className="dz-block-head">
        <span className="dz-block-n">{n}</span>
        <Icon className="dz-block-icon" size={20} />
        <h3 className="dz-block-title">{title}</h3>
        {extra && <div className="dz-block-extra">{extra}</div>}
      </header>
      <div className="dz-block-body">{children}</div>
    </section>
  );
}

/** Quick-pick tokens under a field — one tap instead of typing. */
function Chips({ options, isOn, onToggle }) {
  return (
    <div className="dz-chips">
      {options.map((o) => (
        <button
          key={o}
          type="button"
          className={`dz-chip${isOn(o) ? ' is-on' : ''}`}
          onClick={() => onToggle(o)}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

export default function NewDossier({ tabs, active }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToastWithSound();

  const [draft, setDraft] = useState(() => loadDraft(user?.id) || M.freshDraft());
  const [errors, setErrors] = useState({});
  const [reviewing, setReviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);
  const [printing, setPrinting] = useState(null); // 'dossier' | 'record'
  const [extraFamilies, setExtraFamilies] = useState([]);
  const [editingName, setEditingName] = useState(false);
  const [clearArmed, setClearArmed] = useState(false);
  const locked = !!saved;

  // Everything the form looks things up in, in two requests: pupils (for
  // "already registered?"), families (existing family + sibling match),
  // active sections with their counts, and the buses with their fees.
  const { data: ref } = useCachedData(
    () => Promise.all([api.searchIndex(), api.transportation()]).then(([idx, buses]) => ({ idx, buses })),
    [],
    { key: 'dossier-ref', ttl: TTL.ROSTER }
  );
  const students = useMemo(() => ref?.idx?.students || [], [ref]);
  const sections = useMemo(() => ref?.idx?.sections || [], [ref]);
  const buses = useMemo(() => (ref?.buses || []).filter((b) => b.status !== 'inactive'), [ref]);
  // A family added a moment ago is known here before the refetched list
  // arrives, so «إضبارة لأخ/أخت» can select it straight away.
  const families = useMemo(() => {
    const list = ref?.idx?.families || [];
    const known = new Set(list.map((f) => f.id));
    return [...extraFamilies.filter((f) => !known.has(f.id)), ...list];
  }, [ref, extraFamilies]);

  // The draft survives a page change or a reload (this tab only; cleared
  // on sign-in/out with the other drafts in ui/api.js).
  useEffect(() => {
    if (locked) return;
    const id = setTimeout(() => {
      try { sessionStorage.setItem(draftKey(user?.id), JSON.stringify(draft)); } catch { /* full or blocked */ }
    }, 300);
    return () => clearTimeout(id);
  }, [draft, locked, user]);

  const selectedFamily = useMemo(
    () => (draft.familyMode === 'existing' ? families.find((f) => String(f.id) === String(draft.familyId)) || null : null),
    [draft.familyMode, draft.familyId, families]
  );
  const effFamily = draft.familyMode === 'new' ? draft.family : selectedFamily;
  const autoName = M.composeName(draft.student.first_name, effFamily);
  const fullName = draft.nameTouched ? draft.name : autoName;
  const full = useMemo(() => ({ ...draft, name: fullName }), [draft, fullName]);

  const section = sections.find((s) => String(s.id) === String(draft.student.section_id)) || null;
  const bus = buses.find((b) => String(b.id) === String(draft.student.transportation_id)) || null;
  const progress = M.missingItems(full, effFamily);
  const dup = useMemo(() => (locked ? { byNid: [], byName: [] } : M.matchStudents(full, students)), [full, students, locked]);
  const familyHits = useMemo(
    () => (draft.familyMode === 'new' && !locked ? M.matchFamilies(draft.family, families) : []),
    [draft.familyMode, draft.family, families, locked]
  );
  const age = M.ageText(draft.student.birth_date);

  const set = useCallback((group, k, v) => {
    setDraft((d) => ({ ...d, [group]: { ...d[group], [k]: v } }));
    setErrors((e) => (e[`${group}.${k}`] ? { ...e, [`${group}.${k}`]: null } : e));
  }, []);

  const setBus = (v) => {
    const b = buses.find((x) => String(x.id) === String(v));
    setDraft((d) => ({ ...d, student: { ...d.student, transportation_id: v, transport_fee: b ? String(b.monthly_fee || 0) : '' } }));
  };

  const switchFamilyMode = (mode) => {
    setDraft((d) => ({ ...d, familyMode: mode }));
    setErrors((e) => ({ ...e, familyId: null, 'family.father_name': null }));
  };

  const linkFamily = (f) => {
    setDraft((d) => ({ ...d, familyMode: 'existing', familyId: String(f.id) }));
    setErrors({});
    toast.info(`رُبط الطالب بعائلة ${[f.father_name, M.familySurname(f)].filter(Boolean).join(' ')}`);
  };

  // --- «تأكيد» → review popup → «إضافة» ---------------------------------
  const confirm = useCallback(() => {
    if (locked) return;
    const e = M.validate(full);
    setErrors(e);
    const first = Object.keys(e).find((k) => e[k]);
    if (first) {
      toast.error(e[first]);
      focusField(first === 'name' ? 'student.first_name' : first);
      return;
    }
    setReviewing(true);
  }, [full, locked, toast]);

  const add = async () => {
    setSaving(true);
    try {
      const created = await api.createStudent(M.toRequest(full));
      if (draft.familyMode === 'new' && created.family_id) {
        const f = draft.family;
        setExtraFamilies((list) => [{
          id: created.family_id, father_name: f.father_name, mother_name: f.mother_name, last_name: f.last_name,
          father_phone: f.father_phone, mother_phone: f.mother_phone, student_count: 1, student_names: created.name,
        }, ...list]);
      }
      setSaved({ ...created, registrar: user?.name, savedAt: new Date() });
      setReviewing(false);
      setEditingName(false);
      try { sessionStorage.removeItem(draftKey(user?.id)); } catch { /* ignore */ }
      // The directory, «الإضبارات» and this form's look-ups all show the
      // new pupil from now on.
      invalidate('student-directory');
      invalidate('dossier');
      // \u2066…\u2069 (LRI…PDI): a plain-text toast would print "#060008" as "060008#".
      toast.success(`تمت إضافة الطالب — رقم التسجيل \u2066${formatRegNo(created.registration_number)}\u2069`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const startNew = () => {
    setDraft(M.freshDraft());
    setSaved(null);
    setErrors({});
    setEditingName(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => focusField('student.first_name'), 350);
  };

  // A brother or sister: same family, and what siblings share on paper —
  // the civil registry (محل ورقم القيد), the family's situation, the bus
  // and the previous school — carried over; the child's own facts start empty.
  const newSibling = () => {
    const s = draft.student;
    const next = M.freshDraft();
    next.familyMode = 'existing';
    next.familyId = String(saved.family_id);
    Object.assign(next.student, {
      registry_place: s.registry_place, registry_number: s.registry_number,
      family_status: s.family_status, origin_place: s.origin_place,
      transportation_id: s.transportation_id, transport_fee: s.transport_fee,
      tuition_fee: s.tuition_fee,
    });
    setDraft(next);
    setSaved(null);
    setErrors({});
    setEditingName(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => focusField('student.first_name'), 350);
  };

  const clearAll = () => {
    if (!clearArmed) { setClearArmed(true); return; }
    setClearArmed(false);
    startNew();
  };
  useEffect(() => {
    if (!clearArmed) return undefined;
    const id = setTimeout(() => setClearArmed(false), 3000);
    return () => clearTimeout(id);
  }, [clearArmed]);

  // Ctrl+Enter = «تأكيد», from anywhere in the form.
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !reviewing && !printing) {
        e.preventDefault();
        confirm();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [active, confirm, reviewing, printing]);

  // --- field helpers (plain functions, not components: no remount on type) ---
  const err = (key) => errors[key] || undefined;
  const text = (group, k, label, o = {}) => (
    <Field label={label} required={o.required} error={err(`${group}.${k}`)} hint={o.hint} span={o.span} guide={`dz-${group}.${k}`}>
      <Input
        id={`dz-${group}.${k}`}
        value={draft[group][k]}
        onChange={(e) => set(group, k, e.target.value)}
        error={err(`${group}.${k}`)}
        list={o.list}
        dir={o.dir}
        type={o.type || 'text'}
        placeholder={o.placeholder}
        inputMode={o.inputMode}
        max={o.max}
        min={o.min}
        autoComplete="off"
      />
    </Field>
  );
  const phone = (group, k, label) => (
    <Field label={label} error={err(`${group}.${k}`)}>
      <div id={`dz-${group}.${k}`}>
        <PhoneInput value={draft[group][k]} onChange={(v) => set(group, k, v)} disabled={locked} />
      </div>
    </Field>
  );

  const sectionOptions = useMemo(() => sections.map((s) => ({
    value: s.id,
    label: `${s.section_label} — ${s.student_count || 0} طالب`,
  })), [sections]);
  const familyOptions = useMemo(() => families.map((f) => ({
    value: f.id,
    label: `${[f.father_name, M.familySurname(f)].filter(Boolean).join(' ') || `عائلة #${f.id}`}`
      + (f.mother_name ? ` و${f.mother_name}` : '')
      + (f.father_phone || f.mother_phone ? ` — ${formatPhone(f.father_phone || f.mother_phone)}` : '')
      + (f.student_count > 0 ? ` (${f.student_count} طلاب)` : ''),
  })), [families]);
  const busOptions = buses.map((b) => ({
    value: b.id,
    label: `${b.bus_number} — ${b.driver_name} (${money(b.monthly_fee)}/شهر)`,
  }));

  return (
    <PageShell
      title="شؤون الطلاب"
      description="إضبارة جديدة: بيانات الطالب والعائلة والتسجيل في صفحة واحدة، ثم المراجعة والإضافة والطباعة"
      actions={<HelpButton guides={['student-to-section']} />}
    >
      {tabs}

      {saved && (
        <div className="dz-success" role="status">
          <span className="dz-success-icon"><MdCheckCircle size={30} /></span>
          <div className="dz-success-text">
            <strong>تمت إضافة الطالب {saved.name}</strong>
            <span>
              رقم التسجيل <b className="dz-num">{formatRegNo(saved.registration_number)}</b>
              {saved.section_label ? ` · ${saved.section_label}` : ' · بلا شعبة'}
            </span>
          </div>
          <div className="dz-success-actions">
            <Button onClick={() => setPrinting('dossier')}><MdPrint size={17} /> طباعة الإضبارة</Button>
            <Button variant="secondary" onClick={() => setPrinting('record')}><MdDescription size={17} /> وثيقة طالب</Button>
          </div>
        </div>
      )}

      <div className={`dz-layout${locked ? ' dz-locked' : ''}`}>
        <div className="dz-main">
          <fieldset className="dz-fieldset" disabled={locked}>
            {/* The paper form's header line: the number the school gives,
                and the day the dossier is opened. */}
            <div className="dz-strip">
              <div className="dz-strip-item">
                <label className="ui-field-label" htmlFor="dz-regno">رقم التسجيل</label>
                <input
                  id="dz-regno"
                  className={`ui-input dz-regno${saved ? ' is-new' : ''}`}
                  value={saved ? formatRegNo(saved.registration_number) : ''}
                  placeholder="يُعطى تلقائياً عند الإضافة"
                  readOnly
                  dir="ltr"
                />
              </div>
              <div className="dz-strip-item">
                {text('student', 'registration_date', 'تاريخ التسجيل', { type: 'date' })}
              </div>
              <div className="dz-strip-item dz-strip-hint">
                <MdKeyboardReturn size={16} /> <span><b>Ctrl + Enter</b> للتأكيد</span>
              </div>
            </div>

            <Block n="1" icon={MdPerson} title="الطالب">
              <div className="ui-form-grid ui-form-grid-3">
                {text('student', 'first_name', 'الاسم', { required: true, placeholder: 'سامي' })}
                {text('student', 'grandfather_name', 'اسم الجد')}
                <Field label="الجنس">
                  <div id="dz-student.gender">
                    <RadioGroup options={GENDERS} value={draft.student.gender} onChange={(v) => set('student', 'gender', v)} />
                  </div>
                </Field>
                {text('student', 'birth_date', 'تاريخ الميلاد', { type: 'date', max: M.localToday(), hint: age ? `العمر: ${age}` : undefined })}
                {text('student', 'birth_place', 'مكان الولادة', { list: 'dz-places' })}
                {text('student', 'national_id', 'الرقم الوطني', { dir: 'ltr', inputMode: 'numeric' })}
                {phone('student', 'student_phone', 'هاتف الطالب')}
              </div>

              <div className={`dz-fullname${err('name') && !fullName ? ' has-error' : ''}`}>
                <span className="dz-fullname-label">الاسم الكامل</span>
                {editingName ? (
                  <Input
                    id="dz-name"
                    autoFocus
                    value={fullName}
                    onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value, nameTouched: true }))}
                    onBlur={() => setEditingName(false)}
                  />
                ) : (
                  <strong className="dz-fullname-value">{fullName || '—'}</strong>
                )}
                {!locked && !editingName && (
                  <button type="button" className="dz-link" onClick={() => setEditingName(true)}>
                    <MdEdit size={14} /> تعديل
                  </button>
                )}
                {!locked && draft.nameTouched && (
                  <button
                    type="button"
                    className="dz-link"
                    onClick={() => setDraft((d) => ({ ...d, name: '', nameTouched: false }))}
                  >
                    تركيب تلقائي
                  </button>
                )}
              </div>

              {(dup.byNid.length > 0 || dup.byName.length > 0) && (
                <div className="dz-warn">
                  <MdWarningAmber size={18} />
                  <div>
                    {dup.byNid.map((s) => (
                      <p key={`n${s.id}`}>
                        الرقم الوطني مسجّل للطالب <b>{s.name}</b>
                        {s.section_label ? ` (${s.section_label})` : ''}{s.status !== 'active' ? ' — مغادر' : ''}.{' '}
                        <button type="button" className="dz-link" onClick={() => navigate('student-directory', { studentId: s.id })}>فتح ملفه</button>
                      </p>
                    ))}
                    {dup.byName.filter((s) => !dup.byNid.some((x) => x.id === s.id)).map((s) => (
                      <p key={`m${s.id}`}>
                        يوجد طالب بنفس الاسم: <b>{s.name}</b>
                        {s.section_label ? ` (${s.section_label})` : ''}{s.status !== 'active' ? ' — مغادر' : ''}.{' '}
                        <button type="button" className="dz-link" onClick={() => navigate('student-directory', { studentId: s.id })}>فتح ملفه</button>
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </Block>

            <Block
              n="2"
              icon={MdFamilyRestroom}
              title="العائلة"
              extra={<RadioGroup options={FAMILY_MODES} value={draft.familyMode} onChange={switchFamilyMode} />}
            >
              {draft.familyMode === 'new' ? (
                <>
                  <div className="ui-form-grid ui-form-grid-3">
                    {text('family', 'father_name', 'اسم الأب', { required: true })}
                    {text('family', 'last_name', 'الكنية', { hint: 'تُضاف إلى اسم الطالب' })}
                    {text('family', 'father_job', 'عمل الأب')}
                    {phone('family', 'father_phone', 'هاتف الأب')}
                    {text('family', 'mother_name', 'اسم الأم')}
                    {text('family', 'mother_last_name', 'كنية الأم')}
                    {phone('family', 'mother_phone', 'هاتف الأم')}
                    {text('family', 'amana', 'الأمانة', { list: 'dz-amana' })}
                    {text('family', 'address', 'العنوان بالتفصيل')}
                  </div>
                  {familyHits.length > 0 && (
                    <div className="dz-match">
                      <div className="dz-match-title"><MdFamilyRestroom size={17} /> هذه العائلة مسجّلة في المدرسة؟</div>
                      {familyHits.map(({ family: f, by }) => (
                        <div className="dz-match-row" key={f.id}>
                          <div className="dz-match-text">
                            <b>{[f.father_name, M.familySurname(f)].filter(Boolean).join(' ') || `عائلة #${f.id}`}</b>
                            {f.mother_name ? ` و${f.mother_name}` : ''}
                            <span className="dz-match-why">{by === 'phone' ? 'نفس رقم الهاتف' : 'نفس اسم الأب والكنية'}</span>
                            {f.student_names && <span className="dz-match-kids">الأبناء: {f.student_names}</span>}
                          </div>
                          <Button variant="secondary" onClick={() => linkFamily(f)}><MdLink size={16} /> ربط الطالب بها</Button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <Field label="ابحث عن العائلة (اسم الأب أو الأم أو الهاتف)" required error={err('familyId')} guide="dz-familyId">
                    <div id="dz-familyId">
                      <SearchableSelect
                        value={draft.familyId}
                        onChange={(v) => { setDraft((d) => ({ ...d, familyId: v ? String(v) : '' })); setErrors((e) => ({ ...e, familyId: null })); }}
                        options={familyOptions}
                        placeholder="اكتب اسم الأب أو رقم الهاتف..."
                        emptyMessage="لا توجد عائلة بهذا الاسم — اختر «عائلة جديدة»"
                        disabled={locked}
                        error={!!err('familyId')}
                      />
                    </div>
                  </Field>
                  {selectedFamily && (
                    <div className="dz-family-card">
                      <div><span>الأب</span><b>{selectedFamily.father_name || '—'}</b></div>
                      <div><span>الكنية</span><b>{M.familySurname(selectedFamily) || '—'}</b></div>
                      <div><span>الأم</span><b>{selectedFamily.mother_name || '—'}</b></div>
                      <div><span>هاتف الأب</span><b dir="ltr">{selectedFamily.father_phone ? formatPhone(selectedFamily.father_phone) : '—'}</b></div>
                      <div><span>هاتف الأم</span><b dir="ltr">{selectedFamily.mother_phone ? formatPhone(selectedFamily.mother_phone) : '—'}</b></div>
                      <div className="dz-family-kids">
                        <span>الأبناء المسجّلون ({selectedFamily.student_count || 0})</span>
                        <b>{selectedFamily.student_names || '—'}</b>
                      </div>
                    </div>
                  )}
                </>
              )}
            </Block>

            <Block n="3" icon={MdBadge} title="القيد المدني والأوراق الثبوتية">
              <div className="ui-form-grid ui-form-grid-3">
                <Field label="الأوراق الثبوتية" span>
                  <div id="dz-student.id_document_type">
                    <Chips
                      options={M.ID_DOCS}
                      isOn={(o) => M.hasToken(draft.student.id_document_type, o)}
                      onToggle={(o) => set('student', 'id_document_type', M.toggleToken(draft.student.id_document_type, o))}
                    />
                  </div>
                </Field>
                {text('student', 'registry_place', 'محل القيد', { list: 'dz-registry' })}
                {text('student', 'registry_number', 'رقم القيد', { dir: 'ltr' })}
                <Field label="الحالة العائلية">
                  <Select
                    id="dz-student.family_status"
                    value={draft.student.family_status}
                    onChange={(e) => set('student', 'family_status', e.target.value)}
                    options={M.FAMILY_STATUS}
                    placeholder="—"
                  />
                </Field>
                {text('student', 'health_status', 'الوضع الصحي', { list: 'dz-health', placeholder: 'سليم' })}
              </div>
            </Block>

            <Block n="4" icon={MdSchool} title="التسجيل الدراسي">
              <div className="ui-form-grid ui-form-grid-3">
                <Field
                  label="الشعبة"
                  span
                  guide="dz-student.section_id"
                  hint={section
                    ? `فيها الآن ${section.student_count || 0} طالب${section.homeroom_teacher_name ? ` · معلم الصف: ${section.homeroom_teacher_name}` : ''}`
                    : sections.length === 0 ? 'لا توجد شعب بعد — يمكن إضافة الطالب وتسجيله في شعبة لاحقاً' : undefined}
                >
                  <div id="dz-student.section_id">
                    <SearchableSelect
                      value={draft.student.section_id}
                      onChange={(v) => set('student', 'section_id', v ? String(v) : '')}
                      options={sectionOptions}
                      placeholder="اكتب اسم الصف أو الشعبة..."
                      emptyMessage="لا توجد شعبة بهذا الاسم"
                      disabled={locked}
                    />
                  </div>
                </Field>
                {text('student', 'origin_place', 'وافد من (المدرسة السابقة)', { list: 'dz-origins' })}
                {text('student', 'displacement_status', 'وثائق ثبوتية دراسية', { placeholder: 'جلاء سابع + ثامن' })}
                {text('student', 'photos', 'صور شخصية (العدد)', { type: 'number', min: 0, max: 20, inputMode: 'numeric' })}
                <div className="ui-field ui-field-span dz-chips-row">
                  <Chips
                    options={M.SCHOOL_DOCS}
                    isOn={(o) => M.hasToken(draft.student.displacement_status, o)}
                    onToggle={(o) => set('student', 'displacement_status', M.toggleToken(draft.student.displacement_status, o))}
                  />
                </div>
              </div>
            </Block>

            <Block n="5" icon={MdPayments} title="الأقساط والمواصلات">
              <div className="ui-form-grid ui-form-grid-3">
                {text('student', 'tuition_fee', 'القسط الشهري ($)', { type: 'number', min: 0, inputMode: 'numeric', placeholder: '0 = بلا رسوم' })}
                <Field label="الباص" hint={buses.length === 0 ? 'لا توجد باصات مسجّلة' : undefined}>
                  <Select
                    id="dz-student.transportation_id"
                    value={draft.student.transportation_id}
                    onChange={(e) => setBus(e.target.value)}
                    options={busOptions}
                    placeholder="بدون مواصلات"
                  />
                </Field>
                <Field label="قسط النقل ($)" hint={bus ? 'من رسم الباص' : undefined}>
                  <Input
                    id="dz-student.transport_fee"
                    type="number"
                    min={0}
                    value={draft.student.transport_fee}
                    onChange={(e) => set('student', 'transport_fee', e.target.value)}
                    disabled={!!bus}
                  />
                </Field>
              </div>
            </Block>

            <Block n="6" icon={MdStickyNote2} title="ملاحظات">
              <Textarea
                id="dz-student.notes"
                rows={3}
                value={draft.student.notes}
                onChange={(e) => set('student', 'notes', e.target.value)}
                placeholder="أي ملاحظة تُحفظ في ملف الطالب"
              />
              <Chips
                options={M.NOTE_CHIPS}
                isOn={(o) => String(draft.student.notes || '').split('\n').map((l) => l.trim()).includes(o)}
                onToggle={(o) => set('student', 'notes', M.toggleNoteLine(draft.student.notes, o))}
              />
            </Block>
          </fieldset>

          <div className={`dz-actions${locked ? ' is-saved' : ''}`}>
            {locked ? (
              <>
                <span className="dz-actions-done"><MdCheckCircle size={20} /> أُضيف برقم <b className="dz-num">{formatRegNo(saved.registration_number)}</b></span>
                <Button onClick={() => setPrinting('dossier')}><MdPrint size={17} /> طباعة الإضبارة</Button>
                <Button variant="secondary" onClick={() => navigate('student-directory', { studentId: saved.id })}>
                  <MdOpenInNew size={16} /> فتح ملف الطالب
                </Button>
                <Button variant="secondary" onClick={newSibling}><MdGroupAdd size={17} /> إضبارة لأخ/أخت</Button>
                <Button variant="success" onClick={startNew}><MdCreateNewFolder size={17} /> إضبارة جديدة</Button>
              </>
            ) : (
              <>
                <Button variant="success" onClick={confirm} data-guide="dz-confirm">
                  <MdCheckCircle size={19} /> تأكيد
                </Button>
                <Button variant={clearArmed ? 'danger' : 'secondary'} onClick={clearAll}>
                  <MdRestartAlt size={17} /> {clearArmed ? 'اضغط مرة أخرى للمسح' : 'مسح الحقول'}
                </Button>
                <span className="dz-actions-hint">
                  {progress.percent < 100 ? `الإضبارة مكتملة ${progress.percent}%` : 'الإضبارة مكتملة ✓'}
                </span>
              </>
            )}
          </div>
        </div>

        <DossierCard
          name={fullName}
          gender={draft.student.gender}
          section={section}
          family={effFamily}
          age={age}
          progress={progress}
          saved={saved}
          onMissing={focusField}
          onList={() => navigate('student-directory', { view: 'dossiers' })}
        />
      </div>

      <datalist id="dz-places">{M.PLACES.map((p) => <option key={p} value={p} />)}</datalist>
      <datalist id="dz-amana">{M.AMANA.map((p) => <option key={p} value={p} />)}</datalist>
      <datalist id="dz-registry">{M.REGISTRY_PLACES.map((p) => <option key={p} value={p} />)}</datalist>
      <datalist id="dz-origins">{M.ORIGINS.map((p) => <option key={p} value={p} />)}</datalist>
      <datalist id="dz-health">{M.HEALTH_STATUS.map((p) => <option key={p} value={p} />)}</datalist>

      {reviewing && (
        <ReviewModal
          draft={full}
          family={effFamily}
          section={section}
          bus={bus}
          dup={dup}
          progress={progress}
          saving={saving}
          onBack={() => setReviewing(false)}
          onAdd={add}
        />
      )}

      {printing && saved && (
        <DossierPrint
          studentId={saved.id}
          kind={printing}
          registrarName={saved.registrar}
          onClose={() => setPrinting(null)}
        />
      )}
    </PageShell>
  );
}

/* ------------------------------------------------------------------ */
/* The live dossier: what is being registered, as it is typed          */
/* ------------------------------------------------------------------ */

function DossierCard({ name, gender, section, family, age, progress, saved, onMissing, onList }) {
  const r = 30;
  const c = 2 * Math.PI * r;
  const tone = progress.percent >= 80 ? 'success' : progress.percent >= 50 ? 'info' : 'warning';
  const parents = [family?.father_name, family?.mother_name].filter(Boolean);
  const child = gender === 'female' ? 'ابنة' : 'ابن';

  return (
    <aside className="dz-side">
      <div className={`dz-folder${saved ? ' is-saved' : ''}`}>
        <div className="dz-folder-tab">
          <MdFolderShared size={16} />
          {saved ? <>إضبارة <b className="dz-num">{formatRegNo(saved.registration_number)}</b></> : 'إضبارة جديدة'}
        </div>
        <div className="dz-folder-body">
          <div className={`dz-avatar${gender ? ` is-${gender}` : ''}`}>{M.initials(name)}</div>
          <div className="dz-folder-name">{name || 'اسم الطالب'}</div>
          <div className="dz-folder-sub">{section ? section.section_label : 'لم تُحدَّد الشعبة'}</div>
          <div className="dz-folder-meta">
            {age && <span><MdCake size={14} /> {age}</span>}
            {parents.length > 0 && <span>{child} {parents.join(' و')}</span>}
          </div>
          {saved && (
            <div className="dz-stamp" aria-hidden="true">
              <span>تمت الإضافة</span>
              <small>{saved.savedAt.toLocaleDateString('en-GB')} · {saved.registrar}</small>
            </div>
          )}
        </div>

        {!saved && (
          <div className="dz-progress">
            <svg viewBox="0 0 72 72" className={`dz-ring is-${tone}`} aria-hidden="true">
              <circle cx="36" cy="36" r={r} className="dz-ring-track" />
              <circle
                cx="36"
                cy="36"
                r={r}
                className="dz-ring-fill"
                strokeDasharray={c}
                strokeDashoffset={c * (1 - progress.percent / 100)}
              />
            </svg>
            <div className="dz-progress-text">
              <b>{progress.percent}%</b>
              <span>اكتمال الإضبارة</span>
            </div>
          </div>
        )}

        {!saved && progress.missing.length > 0 && (
          <div className="dz-missing">
            <span className="dz-missing-title">ينقصها:</span>
            {progress.missing.map((m) => (
              <button key={m.field} type="button" className="dz-missing-item" onClick={() => onMissing(m.field)}>
                {m.label}
              </button>
            ))}
          </div>
        )}

        {saved && (
          <button type="button" className="dz-folder-link" onClick={onList}>
            <MdFolderShared size={16} /> كل إضبارات اليوم
          </button>
        )}
      </div>
    </aside>
  );
}

/* ------------------------------------------------------------------ */
/* «تأكيد» → review everything → «إضافة»                               */
/* ------------------------------------------------------------------ */

function ReviewGroup({ title, badge, rows }) {
  const shown = rows.filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (!shown.length && !badge) return null;
  return (
    <div className="dz-review-group">
      <div className="dz-review-title">{title}{badge && <span className="ui-badge ui-badge-info">{badge}</span>}</div>
      <dl className="dz-review-grid">
        {shown.map(([label, value, ltr]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd dir={ltr ? 'ltr' : undefined}>{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function ReviewModal({ draft, family, section, bus, dup, progress, saving, onBack, onAdd }) {
  const s = draft.student;
  const f = family || {};
  const gender = s.gender === 'male' ? 'ذكر' : s.gender === 'female' ? 'أنثى' : '';
  const tuition = Number(s.tuition_fee) || 0;
  const transport = bus ? bus.monthly_fee || 0 : Number(s.transport_fee) || 0;

  return (
    <Modal
      open
      size="lg"
      title="مراجعة الإضبارة قبل الإضافة"
      onClose={onBack}
      footer={(
        <>
          <Button variant="secondary" onClick={onBack} disabled={saving}>رجوع للتعديل</Button>
          <Button variant="success" onClick={onAdd} loading={saving} autoFocus data-guide="dz-add">
            <MdCheckCircle size={18} /> إضافة
          </Button>
        </>
      )}
    >
      <div className="dz-review">
        <div className="dz-review-head">
          <div className={`dz-avatar is-small${s.gender ? ` is-${s.gender}` : ''}`}>{M.initials(draft.name)}</div>
          <div>
            <div className="dz-review-name">{draft.name}</div>
            <div className="dz-review-sub">
              {section ? section.section_label : 'بلا شعبة'} · رقم التسجيل يُعطى تلقائياً عند الإضافة
            </div>
          </div>
        </div>

        {(dup.byNid.length > 0 || dup.byName.length > 0) && (
          <div className="dz-warn">
            <MdWarningAmber size={18} />
            <div>
              {dup.byNid.map((x) => <p key={`n${x.id}`}>الرقم الوطني مسجّل للطالب <b>{x.name}</b>.</p>)}
              {dup.byName.filter((x) => !dup.byNid.some((y) => y.id === x.id))
                .map((x) => <p key={`m${x.id}`}>يوجد طالب بنفس الاسم: <b>{x.name}</b>{x.section_label ? ` (${x.section_label})` : ''}.</p>)}
            </div>
          </div>
        )}
        {progress.missing.length > 0 && (
          <div className="dz-review-missing">
            لم يُدخل بعد: {progress.missing.map((m) => m.label).join('، ')}
          </div>
        )}

        <ReviewGroup
          title="الطالب"
          rows={[
            ['الاسم الكامل', draft.name],
            ['اسم الجد', s.grandfather_name],
            ['الجنس', gender],
            ['تاريخ الميلاد', s.birth_date, true],
            ['مكان الولادة', s.birth_place],
            ['الرقم الوطني', s.national_id, true],
            ['هاتف الطالب', s.student_phone ? formatPhone(s.student_phone) : '', true],
          ]}
        />
        <ReviewGroup
          title="العائلة"
          badge={draft.familyMode === 'new' ? 'عائلة جديدة' : 'عائلة مسجّلة'}
          rows={[
            ['اسم الأب', f.father_name],
            ['الكنية', M.familySurname(f)],
            ['عمل الأب', f.father_job],
            ['هاتف الأب', f.father_phone ? formatPhone(f.father_phone) : '', true],
            ['اسم الأم', f.mother_name],
            ['كنية الأم', f.mother_last_name],
            ['هاتف الأم', f.mother_phone ? formatPhone(f.mother_phone) : '', true],
            ['الأمانة', f.amana],
            ['العنوان', f.address],
            ['الأبناء المسجّلون', draft.familyMode === 'existing' ? f.student_names : ''],
          ]}
        />
        <ReviewGroup
          title="القيد المدني والأوراق الثبوتية"
          rows={[
            ['الأوراق الثبوتية', s.id_document_type],
            ['محل القيد', s.registry_place],
            ['رقم القيد', s.registry_number, true],
            ['الحالة العائلية', s.family_status],
            ['الوضع الصحي', s.health_status],
          ]}
        />
        <ReviewGroup
          title="التسجيل الدراسي"
          rows={[
            ['الشعبة', section ? section.section_label : 'بلا شعبة'],
            ['تاريخ التسجيل', s.registration_date, true],
            ['وافد من', s.origin_place],
            ['وثائق ثبوتية دراسية', s.displacement_status],
            ['صور شخصية', Number(s.photos) > 0 ? String(Number(s.photos)) : ''],
          ]}
        />
        <ReviewGroup
          title="الأقساط والمواصلات"
          rows={[
            ['القسط الشهري', tuition ? money(tuition) : 'بلا رسوم'],
            ['الباص', bus ? `${bus.bus_number} — ${bus.driver_name}` : 'بدون مواصلات'],
            ['قسط النقل', transport ? money(transport) : ''],
          ]}
        />
        {s.notes && <ReviewGroup title="ملاحظات" rows={[['', s.notes]]} />}
      </div>
    </Modal>
  );
}
